import type {FormRequest} from 'src/api/ProactApiClient';
import {FormStateEnum} from 'src/api/ProactApiClient';
import type {OutboxExecutor} from 'src/offline/executorRegistry';
import {rewriteUploadPlaceholders} from 'src/offline/pendingUploads';
import {formApi} from 'src/redux/queries/form';
import {store} from 'src/redux/store';
import {createForm, getForm, transitionForm} from './api';
import type {OfflineFormCreateRequest} from './queuedTransition';
import {stripQueuedTransition} from './queuedTransition';

// A read failure counts as "still draft" so the original transition error is the
// one reported and classified.
const isStillDraft = async (id: number): Promise<boolean> => {
  try {
    return (await getForm(id)).state === FormStateEnum.Draft;
  } catch {
    return true;
  }
};

export const createFormExecutor: OutboxExecutor = async (
  payload,
  clientSubmissionId,
  ctx
) => {
  const source = payload as OfflineFormCreateRequest;
  const request: FormRequest = {
    ...stripQueuedTransition(source),
    clientSubmissionId,
    targetFieldValues: (source.targetFieldValues ?? []).map(fieldValue =>
      typeof fieldValue.value === 'string'
        ? {...fieldValue, value: rewriteUploadPlaceholders(fieldValue.value, ctx)}
        : fieldValue
    ),
  };
  const response = await createForm(request);

  const queued = source.queuedTransition;
  if (queued) {
    try {
      await transitionForm(response.id, {transition: queued.name, note: ''});
    } catch (error) {
      // A replayed transition may already have landed before the crash; only
      // report the error when the form is still a draft.
      if (await isStillDraft(response.id)) throw error;
    }
  }

  // Swap the pending row for the real submission in any open table without a
  // reload; fires only on a successful create, never from the sync interval.
  store.dispatch(formApi.util.invalidateTags(['Forms', 'TaskAssignments']));
  return {
    summary: queued
      ? `Form #${response.id} created and ${queued.name}`
      : `Form #${response.id} created`,
  };
};
